/**
 * position.js — le téléphone dit où il est, sans qu'on le lui demande
 *
 * Aucun bouton. Le technicien ouvre son planning le matin, et c'est tout :
 * l'application échantillonne sa position pendant qu'elle est au premier plan
 * et la dépose sur le serveur, qui la rapproche des rendez-vous du jour.
 *
 * Deux choix méritent d'être dits :
 *
 *   — **On échantillonne, on ne suit pas.** `watchPosition` garde le GPS
 *     allumé toute la journée et vide la batterie ; un relevé toutes les deux
 *     minutes avec `getCurrentPosition` coûte quelques secondes de GPS et
 *     suffit largement pour un rayon de cinq cents mètres.
 *
 *   — **Rien quand l'application est en arrière-plan.** Le navigateur bride
 *     les minuteries d'un onglet caché et refuse souvent la géolocalisation :
 *     insister ne donnerait que des échecs. Le relevé reprend au retour, et
 *     c'est la limite assumée du dispositif — le serveur la connaît et marque
 *     les départs qu'il en déduit comme estimés.
 *
 * Le refus de permission est remonté au serveur. Sans cela, la page « Où
 * sont-ils » afficherait une case vide, qu'on ne sait pas interpréter : panne,
 * jour de repos, ou géolocalisation coupée dans les réglages du téléphone.
 */

import { envoyerPosition, signalerRefusPosition } from './api';

const PERIODE_MS = 2 * 60 * 1000;   // un relevé toutes les deux minutes
const AGE_TOLERE_MS = 60 * 1000;    // on accepte un point du navigateur vieux d'une minute

let minuterie = null;
let actif = false;
let refuse = false;
let etat = { actif: false, dernier: null, ici: null, erreur: null };
const abonnes = new Set();

function diffuser() {
  for (const f of abonnes) { try { f(etat); } catch { /* un abonné cassé n'arrête rien */ } }
}

/** S'abonner à l'état du suivi. Rend la fonction de désabonnement. */
export function surSuivi(f) {
  abonnes.add(f);
  f(etat);
  return () => abonnes.delete(f);
}

export const etatSuivi = () => etat;

function deposer(pos) {
  const c = pos.coords;
  envoyerPosition({
    lat: c.latitude,
    lon: c.longitude,
    precision_m: c.accuracy === null || c.accuracy === undefined ? null : Math.round(c.accuracy),
    at: new Date(pos.timestamp || Date.now()).toISOString(),
    source: etat.dernier ? 'suivi' : 'ouverture',
  })
    .then(r => {
      etat = { ...etat, dernier: new Date().toISOString(), ici: (r && r.ici) || null, erreur: null };
      diffuser();
    })
    .catch(() => { /* le relevé suivant repassera : rien à dire au technicien */ });
}

function echouer(err) {
  // 1 = permission refusée. C'est la seule qui soit définitive pour la session :
  // les deux autres (position indisponible, délai dépassé) arrivent dans un
  // parking souterrain et se résolvent toutes seules.
  if (err && err.code === 1) {
    if (!refuse) {
      refuse = true;
      signalerRefusPosition('permission refusée sur le téléphone').catch(() => {});
    }
    arreter();
    etat = { ...etat, actif: false, erreur: 'refus' };
    diffuser();
    return;
  }
  etat = { ...etat, erreur: 'indisponible' };
  diffuser();
}

function relever() {
  if (!navigator.geolocation || document.visibilityState === 'hidden') return;
  navigator.geolocation.getCurrentPosition(deposer, echouer, {
    enableHighAccuracy: true,
    timeout: 20000,
    maximumAge: AGE_TOLERE_MS,
  });
}

function auRetour() {
  if (document.visibilityState === 'visible') relever();
}

/** Démarre le suivi. Appelable plusieurs fois sans dommage. */
export function demarrer() {
  if (actif || refuse) return;
  if (!navigator.geolocation) return;
  actif = true;
  etat = { ...etat, actif: true };
  diffuser();

  relever();
  minuterie = setInterval(relever, PERIODE_MS);
  document.addEventListener('visibilitychange', auRetour);
}

/** Arrête le suivi — à la déconnexion. */
export function arreter() {
  if (minuterie) { clearInterval(minuterie); minuterie = null; }
  document.removeEventListener('visibilitychange', auRetour);
  actif = false;
  etat = { ...etat, actif: false };
  diffuser();
}
